import React from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styled, { createGlobalStyle } from 'styled-components';

const MySwal = withReactContent(Swal);

export type ProdutoSemSped = {
  cod_produto: number | string;
  des_produto: string;
};

const SwalEqualButtons = createGlobalStyle`

  .swal2-actions.equal-width {
    width: 100%;
    display: grid !important;
    grid-template-columns: 12.5rem 12.5rem;
    gap: .75rem;
    margin-top: 1rem;
  }
  .swal2-actions.equal-width .swal2-styled {
    margin: 0 !important;
    width: auto;
    white-space: normal;
    min-height: 2.75rem;
    font-weight: 600;
  }
`;

const Wrapper = styled.div`
  display: grid;
  gap: 0.75rem;
  text-align: left;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.35rem;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  opacity: 0.9;
`;

const TableScroll = styled.div`
  max-height: 55vh;
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.6rem 0.8rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    background: var(--swal2-background, #fff);
    z-index: 1;
    font-weight: 600;
    font-size: 0.9rem;
  }

  td {
    font-size: 0.9rem;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

/**
 * Exibe um modal listando todos os produtos sem Tabela SPED.
 *
 * @param itens Lista de produtos (cod_produto e descricao) retornados por `fetchSpedSemClassificacao`.
 * @returns Promise<boolean> → `true` se o usuário clicou em "Gerar mesmo assim", `false` se clicou em "Abortar" ou fechou.
 *
 * @example
 * const continuar = await showProdutosSemSpedModal(itens);
 * if (!continuar) return; // usuário abortou
 * // ... prossiga com a geração
 */
export const showProdutosSemSpedModal = async (
  itens: ProdutoSemSped[],
): Promise<boolean> => {
  if (!Array.isArray(itens) || itens.length === 0) {
    return true; // nada a mostrar; segue o fluxo normal
  }

  const Content: React.FC = () => (
    <>
      <SwalEqualButtons />
      <Wrapper>
        <Title>Produtos sem Tabela SPED informada.</Title>
        <Subtitle>Regularize a situação para presseguir:</Subtitle>

        <TableScroll role="region" aria-label="Produtos sem SPED">
          <Table>
            <thead>
              <tr>
                <th scope="col">Cód. Produto</th>
                <th scope="col">Descrição</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((p) => (
                <tr key={String(p.cod_produto)}>
                  <td>{p.cod_produto}</td>
                  <td>{p.des_produto}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </Wrapper>
    </>
  );

  const result = await MySwal.fire({
    html: <Content />,
    showConfirmButton: true,
    showCancelButton: true,
    confirmButtonText: 'Gerar mesmo assim',
    cancelButtonText: 'Abortar',
    confirmButtonColor: '#8850BF',
    cancelButtonColor: '#DE350B',
    reverseButtons: false,
    focusCancel: true,
    width: 900,
    padding: '1.25rem',
    allowEnterKey: true,
    allowEscapeKey: true,
    allowOutsideClick: () => !Swal.isLoading(),
    customClass: {
      actions: 'equal-width',
    },
  });

  return !!result.isConfirmed;
};
